import React from 'react';
import Text from '@ingka/text';
import { styled } from 'styled-components';
import TimeSlot from '../TimeSlot/TimeSlot';
import Switch from '@ingka/switch';
import locationPin from '@ingka/ssr-icon/paths/location-pin';
import InlineMessage from '@ingka/inline-message';
import { useTranslation, Trans } from 'react-i18next';
import infoCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import { type Slot } from '../../../services/appointment/model';
import { useStateSelector } from '../../../store/';
import {
  selectSelectedOriginalService,
  selectSelectedTimeSlot,
} from '../../../reducers/selectors';
import {
  getPostalCodeForLocation,
  getReplacementServiceTitle,
  getStoreName,
} from '../../../helpers/appHelper';
import { type TFunction } from 'i18next';
import { timeZoneHelper } from '../../../helpers/timeZoneHelper';
import { preventEnterSubmit } from '../../../helpers/formHelper';
import colors from '../../../styles/colors';
import useIANATimeZone from '../../../hooks/useIANATimeZone';

interface TimeSlotsProps {
  slot?: Slot;
  replacementServiceSlot?: Slot;
  onSelectTimeSlot: (id: string, isReplacementService?: boolean) => void;
  onSwitchChange: (show: boolean) => void;
  showReplacementServiceSlots: boolean;
  replacementServiceSlotsEnabled: boolean;
}

const StyledSwitch = styled(Switch)`
  margin-top: 8px;
`;

const StyledFieldSet = styled.fieldset`
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  border: 0;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const StyledSwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 8px;
`;

const NeutralInlineMessage = styled(InlineMessage)`
  &&& {
    svg {
      fill: ${colors.icon.default};
    }
  }
`;

const hasLoadError = (slot?: Slot): boolean => slot?.status === 'error';
const isCompleted = (slot?: Slot): boolean => slot?.status === 'completed';

const hasTimeSlots = (slot?: Slot): boolean =>
  !!slot?.timeSlots.length && isCompleted(slot);

const getTimeSlotsMessage = (t: TFunction, slot?: Slot): string => {
  if (hasLoadError(slot)) {
    return t('pages.timeslots.errorMessage');
  }
  return t('components.timePicker.noAvailableTimeWindowsError');
};

const TimeSlots: React.FC<TimeSlotsProps> = ({
  slot,
  onSelectTimeSlot,
  replacementServiceSlot,
  showReplacementServiceSlots,
  onSwitchChange,
  replacementServiceSlotsEnabled,
}) => {
  const selectedTimeSlot = useStateSelector(selectSelectedTimeSlot);
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const storeId = useStateSelector((state) => state.app.selected.storeId);
  const stores = useStateSelector((state) => state.stores.availableStores);
  const { t, i18n } = useTranslation();
  const replacementServiceStore = useStateSelector(
    (state) => state.app.selected.replacementService?.store,
  );
  const originalService = useStateSelector(selectSelectedOriginalService);
  const replacementService = originalService?.replacementService;
  const storeRemotePostalCode =
    CSS_CONFIG.multipleTimezones && originalService?.location === 'storeRemote'
      ? getPostalCodeForLocation(
          storeId,
          stores,
          CSS_CONFIG,
          originalService.location,
        )
      : '';
  const replacementStoreRemotePostalCode =
    CSS_CONFIG.multipleTimezones &&
    replacementService?.location === 'storeRemote'
      ? replacementServiceStore?.zipCode || ''
      : '';
  const storeRemoteTimeZone = useIANATimeZone(
    storeRemotePostalCode,
    originalService?.serviceId || '',
    originalService?.location,
  );
  const replacementStoreRemoteTimeZone = useIANATimeZone(
    replacementStoreRemotePostalCode,
    replacementService?.serviceId || '',
    replacementService?.location,
  );

  const handleSelectReplacementServiceTimeSlot = React.useCallback(
    (id: string) => {
      onSelectTimeSlot(id, true);
    },
    [onSelectTimeSlot],
  );
  const originalServiceTimeZoneText = originalService?.location
    ? timeZoneHelper(
        originalService.location,
        t,
        CSS_CONFIG,
        storeRemoteTimeZone,
      ).timeZoneTextInfo
    : '';
  const replacementServiceTimeZoneText = replacementService?.location
    ? timeZoneHelper(
        replacementService.location,
        t,
        CSS_CONFIG,
        replacementStoreRemoteTimeZone,
      ).timeZoneTextInfo
    : '';

  return (
    <StyledFieldSet>
      <legend id="timeslots-component-heading">
        <Text tagName="h3" textStyle="Heading.S">
          {`${t('components.timePicker.timeSlotsHeaderText', {
            serviceName: t(`services.name.${serviceId}`),
          })}`}
        </Text>
        {hasTimeSlots(slot) && originalServiceTimeZoneText && (
          <Text>{originalServiceTimeZoneText}</Text>
        )}
      </legend>
      {hasTimeSlots(slot) ? (
        <StyledContainer>
          {slot?.timeSlots.map(({ id, from, to }) => (
            <TimeSlot
              key={id}
              id={id}
              from={from}
              to={to}
              selected={selectedTimeSlot?.id === id}
              onSelectTimeSlot={onSelectTimeSlot}
            />
          ))}
        </StyledContainer>
      ) : (
        <InlineMessage
          variant={hasLoadError(slot) ? 'negative' : 'informative'}
          subtle
          body={getTimeSlotsMessage(t, replacementServiceSlot)}
          ssrIcon={hasLoadError(slot) ? undefined : infoCircleIcon}
        />
      )}
      {replacementServiceSlotsEnabled && (
        <>
          <StyledSwitchContainer>
            <Text>
              {t('components.timePicker.replacementServiceSwitchHeading')}
            </Text>
            <StyledSwitch
              id="replacement-service-switch"
              value="replacement-service-switch"
              checked={showReplacementServiceSlots}
              onKeyDown={preventEnterSubmit}
              onChange={(event) => onSwitchChange(event.target.checked)}
              label={
                <Trans
                  i18n={i18n}
                  i18nKey={
                    'components.timePicker.replacementServiceSwitchLabel'
                  }
                  components={{
                    replacementServiceSwitchLabel: (
                      <strong>
                        {getReplacementServiceTitle(
                          t,
                          t(`services.name.${replacementService?.serviceId}`),
                          replacementService?.location,
                        )}
                      </strong>
                    ),
                  }}
                />
              }
            />
          </StyledSwitchContainer>
          <NeutralInlineMessage
            subtle
            body={getStoreName(
              t,
              replacementServiceStore?.text || '',
              replacementService?.location,
            )}
            ssrIcon={locationPin}
          />
        </>
      )}
      {showReplacementServiceSlots && (
        <>
          {hasTimeSlots(replacementServiceSlot) &&
            replacementServiceTimeZoneText && (
              <Text>{replacementServiceTimeZoneText}</Text>
            )}
          {hasTimeSlots(replacementServiceSlot) ? (
            <StyledContainer>
              {replacementServiceSlot?.timeSlots?.map(({ id, from, to }) => (
                <TimeSlot
                  key={id}
                  id={id}
                  from={from}
                  to={to}
                  selected={selectedTimeSlot?.id === id}
                  onSelectTimeSlot={handleSelectReplacementServiceTimeSlot}
                />
              ))}
            </StyledContainer>
          ) : (
            <InlineMessage
              variant={
                hasLoadError(replacementServiceSlot)
                  ? 'negative'
                  : 'informative'
              }
              subtle
              body={getTimeSlotsMessage(t, replacementServiceSlot)}
              ssrIcon={
                hasLoadError(replacementServiceSlot)
                  ? undefined
                  : infoCircleIcon
              }
            />
          )}
        </>
      )}
    </StyledFieldSet>
  );
};

export default TimeSlots;
